/*
 * VaultCtx — replicates the reference vault-app `useApp()` context shape so the
 * reference pages/modals port near-verbatim, but backed by OUR React Router
 * (go → navigate, real URLs) + local modal/selection/client state.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/** Reference page-key → our real route. */
const ROUTES: Record<string, string> = {
  home: "/home",
  drive: "/vault",
  folder: "/vault",
  ring: "/ring",
  collections: "/collections",
  collection1: "/collections",
  playground: "/studio",
  bulkrender: "/bulk",
  approvals: "/approvals",
  insights: "/insights",
  links: "/links",
  shared: "/vault?tab=shared",
  recent: "/vault?tab=recent",
  starred: "/vault?tab=starred",
  trash: "/vault?tab=deleted",
  render: "/render-settings",
  branding: "/branding",
  s3: "/storage",
  team: "/team",
  billing: "/billing",
  settings: "/settings",
};

/** Our path → reference page-key (for sidebar active state). */
function pageForPath(pathname: string, search: string): string {
  const tab = new URLSearchParams(search).get("tab");
  if (pathname.startsWith("/home")) return "home";
  // /band and /engagement redirect to /ring; keep them mapped so the sidebar
  // highlights correctly during the redirect frame instead of falling through
  // to the "drive" default.
  if (pathname.startsWith("/ring") || pathname.startsWith("/band") || pathname.startsWith("/engagement")) return "ring";
  if (pathname.startsWith("/vault")) {
    if (tab === "shared") return "shared";
    if (tab === "recent") return "recent";
    if (tab === "starred") return "starred";
    if (tab === "deleted") return "trash";
    return "drive";
  }
  if (pathname.startsWith("/collections")) return pathname.includes("/collections/") ? "collection1" : "collections";
  if (pathname.startsWith("/studio")) return "playground";
  if (pathname.startsWith("/bulk")) return "bulkrender";
  if (pathname.startsWith("/approvals")) return "approvals";
  if (pathname.startsWith("/insights")) return "insights";
  if (pathname.startsWith("/links")) return "links";
  if (pathname.startsWith("/render-settings")) return "render";
  if (pathname.startsWith("/branding")) return "branding";
  if (pathname.startsWith("/storage")) return "s3";
  if (pathname.startsWith("/team")) return "team";
  if (pathname.startsWith("/billing")) return "billing";
  // /settings has no sidebar entry — map it explicitly so it doesn't fall through
  // to the "drive" default and wrongly light up "My Drive".
  if (pathname.startsWith("/settings")) return "settings";
  return "drive";
}

export interface VaultCtxValue {
  page: string;
  go: (key: string) => void;
  modals: Record<string, boolean>;
  open: (id: string) => void;
  close: (id: string) => void;
  client: boolean;
  setClient: (v: boolean) => void;
  selected: string[];
  toggleSel: (name: string) => void;
  clearSel: () => void;
  bulkCount: number;
  setBulkCount: (n: number) => void;
  queueExtra: number[];
  setQueueExtra: React.Dispatch<React.SetStateAction<number[]>>;
  navOpen: boolean;
  setNavOpen: (v: boolean) => void;
  /** optional payload the opener can attach for a modal (e.g. which file) */
  modalData: Record<string, unknown>;
  setModalData: (id: string, data: unknown) => void;
  /*
   * Drive revision. The global modals (sidebar "New / Upload") live outside the
   * Drive page and have no handle on its loader, so a folder created or a file
   * uploaded from there used to leave the open listing stale until the user
   * navigated away and back. Anything that mutates drive contents calls
   * bumpDrive(); the Drive page re-fetches when driveRev changes.
   */
  driveRev: number;
  bumpDrive: () => void;
}

const AppCtx = createContext<VaultCtxValue | null>(null);
export const useApp = (): VaultCtxValue => {
  const v = useContext(AppCtx);
  if (!v) throw new Error("useApp must be used within <VaultProvider>");
  return v;
};

export function VaultProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [modals, setModals] = useState<Record<string, boolean>>({});
  const [modalData, setModalDataState] = useState<Record<string, unknown>>({});
  const [client, setClient] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkCount, setBulkCount] = useState(18);
  const [queueExtra, setQueueExtra] = useState<number[]>([]);
  const [navOpen, setNavOpen] = useState(false);
  const [driveRev, setDriveRev] = useState(0);

  const bumpDrive = useCallback(() => setDriveRev((n) => n + 1), []);
  const open = useCallback((id: string) => setModals((m) => ({ ...m, [id]: true })), []);
  const close = useCallback((id: string) => setModals((m) => ({ ...m, [id]: false })), []);
  const setModalData = useCallback((id: string, data: unknown) => setModalDataState((d) => ({ ...d, [id]: data })), []);
  const go = useCallback((key: string) => { navigate(ROUTES[key] ?? key); setNavOpen(false); window.scrollTo(0, 0); }, [navigate]);
  const toggleSel = useCallback((name: string) => setSelected((s) => (s.includes(name) ? s.filter((n) => n !== name) : [...s, name])), []);
  const clearSel = useCallback(() => setSelected([]), []);

  // Reference App.jsx keyboard: Esc clears modals + client view; Ctrl/⌘-K opens cmdk.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setModals({}); setClient(false); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); open("cmdk"); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  const page = pageForPath(location.pathname, location.search);

  const value = useMemo<VaultCtxValue>(() => ({
    page, go, modals, open, close, client, setClient,
    selected, toggleSel, clearSel, bulkCount, setBulkCount,
    queueExtra, setQueueExtra, navOpen, setNavOpen, modalData, setModalData,
    driveRev, bumpDrive,
  }), [page, go, modals, open, close, client, selected, toggleSel, clearSel, bulkCount, queueExtra, navOpen, modalData, setModalData, driveRev, bumpDrive]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
